import React from 'react';
import {
	Speed as SpeedIcon, Security as SecurityIcon, Visibility as VisibilityIcon,
	Public as PublicIcon, CheckCircle as CheckIcon, AccountTree as TreeIcon,
	Layers as LayersIcon, AutoAwesome as AutoAwesomeIcon, Storage as StorageIcon,
	MonetizationOn as MonetizationOnIcon, Payment as PaymentIcon,
	Code as CodeIcon, CloudUpload as CloudUploadIcon, Settings as SettingsIcon,
	Download as DownloadIcon, Group as GroupIcon
} from '@mui/icons-material';
import { getConfig } from '../../../lib/publicConfig';

const USD_PRICE_PER_TOKEN = getConfig('pricing.per_nft_usd', 0.20);

export const featureShowcases = [
	{
		tag: 'Layer Intelligence',
		title: 'Adaptive Layer Orchestration',
		description: 'Visual drag-and-drop interface to model complex trait hierarchies with nested collections, conditional availability, and automated quality checks—no coding required.',
		longDescription: 'Fine-tune how traits stack across hundreds of nested sets using our intuitive no-code builder. Layer orchestration keeps asset pipelines clean, prevents conflicts, and manages fallbacks across every rarity tier—all through visual controls.',
		subFeatures: [
			{
				title: 'Nested Collections & Variants',
				details: 'Create unlimited variant sets for lore factions, special editions, or licensed collaborations. Preserve consistent art direction by linking shared layers while tailoring branch-specific traits.'
			},
			{
				title: 'Quality Assurance Gates',
				details: 'Run preflight scans that detect missing transparency, mismatched dimensions, and conflicting rules before a single preview is generated.'
			},
			{
				title: 'Batch Asset Operations',
				details: 'Upload thousands of assets at once, convert color profiles automatically, and apply naming conventions so everything stays searchable.'
			}
		],
		icon: <LayersIcon sx={{ fontSize: 44, color: '#dc2626' }} />,
		proofPoints: [
			{ label: 'Variants', value: 'Unlimited nested sets' },
			{ label: 'QA Gates', value: 'None placeholders + conflict scans' },
			{ label: 'Upload Speed', value: '5K assets in < 4 minutes' }
		]
	},
	{
		tag: 'Decision Trees',
		title: 'Story-Driven Generation Paths',
		description: 'Branch collections by lore, faction, chain, or supply using visual path designers—no coding needed for weighted routing, reroll logic, and automatic metadata alignment.',
		longDescription: 'Decision trees let narrative directors map entire universes of NFTs without duplicating project files or writing code. Visualize paths from starting trait to final export and adjust probabilities in real time through our no-code interface.',
		subFeatures: [
			{
				title: 'Visual Path Designer',
				details: 'Drag-and-drop path builder with live probability tallies. Quickly branch to special editions or limited run sets.'
			},
			{
				title: 'Conditional Overrides',
				details: 'Trigger path switches whenever rule conditions fire—perfect for storyline unlocks, trait evolutions, or premium drops.'
			},
			{
				title: 'Metadata Harmonization',
				details: 'Standardize metadata across branches with automated schema validation and cross-chain parity checks.'
			}
		],
		icon: <TreeIcon sx={{ fontSize: 44, color: '#94a3b8' }} />,
		proofPoints: [
			{ label: 'Branches', value: 'START → END visual designer' },
			{ label: 'Conditional', value: 'Live rule triggers + overrides' },
			{ label: 'Analytics', value: 'Distribution simulation per path' }
		]
	},
	{
		tag: 'Rarity Science',
		title: 'Probability Engine with Governance',
		description: 'Visual probability controls blend percentage and tier-based rarity without code. Export governance reports and detect distribution outliers before a single NFT leaves staging.',
		longDescription: 'Give rarity specialists the levers they need without sacrificing data integrity or requiring technical skills. Configure probability matrices through intuitive sliders, publish governance reports, and enforce statistical guardrails through every iteration—no coding required.',
		subFeatures: [
			{
				title: 'Multi-Mode Probability',
				details: 'Mix percentage, tier, and conditional boosts in the same project. Switch between modes without losing previous configurations.'
			},
			{
				title: 'Governance Exports',
				details: 'Generate CSV and dashboard reports for compliance, investor updates, and community transparency.'
			},
			{
				title: 'Anomaly Detection',
				details: 'Automatic highlight of impossible combinations, drifted totals, and duplicated weights with suggested fixes.'
			}
		],
		icon: <SpeedIcon sx={{ fontSize: 44, color: '#646cff' }} />,
		proofPoints: [
			{ label: 'Modes', value: 'Weighted, tier, conditional boosts' },
			{ label: 'Reporting', value: 'CSV + dashboard parity' },
			{ label: 'Alerts', value: 'Instant conflict + duplication warnings' }
		]
	},
	{
		tag: 'Security & Provenance',
		title: 'Pixel-Level Authenticity Controls',
		description: 'Invisible watermarking, wallet-gated approvals, and rule-chain auditing protect every asset before, during, and after export.',
		longDescription: 'Protect IP from concept to launch. Security workflows ensure only authorized changes ship, with forensic watermarking and tamper-proof audit logs baked into every generation.',
		subFeatures: [
			{
				title: 'Steganography Watermarking',
				details: 'Embed invisible identifiers at the pixel level that survive compression, cropping, and re-encoding.'
			},
			{
				title: 'Approval Gates',
				details: 'Wallet-gated approvals with delegation support. Every publish action is timestamped and attributable.'
			},
			{
				title: 'Rule-Chain Auditing',
				details: 'Trace how traits and rules interacted for any generated NFT, providing compliance-ready provenance.'
			}
		],
		icon: <SecurityIcon sx={{ fontSize: 44, color: '#ff6b35' }} />,
		proofPoints: [
			{ label: 'Watermarks', value: 'LSB + metadata attestation' },
			{ label: 'Delegations', value: 'Role-aware approvals with logs' },
			{ label: 'Retries', value: 'Duplicate tracker with 100 rerolls' }
		]
	},
	{
		tag: 'Preflight QA',
		title: 'Simulation-First Preview Lab',
		description: 'Generate curated preview sets, run A/B experiments, and bookmark hero outputs to inject straight into live generation jobs.',
		longDescription: 'Preview labs replicate final export conditions so creative teams can approve assets with confidence. Compare variations, bookmark hero outputs, and stream feedback into production queues.',
		subFeatures: [
			{
				title: 'Curated Preview Batches',
				details: 'Generate 1-500 previews with consistent seed replay. Pin favorites for later injection into production runs.'
			},
			{
				title: 'Experimentation Suite',
				details: 'A/B test rarity tables, rule overrides, or art revisions side-by-side with outcome metrics.'
			},
			{
				title: 'Feedback Sync',
				details: 'Commentary threads, approval statuses, and webhook callbacks keep producers and artists aligned.'
			}
		],
		icon: <VisibilityIcon sx={{ fontSize: 44, color: '#9b59b6' }} />,
		proofPoints: [
			{ label: 'Preview Batches', value: '1-500 NFTs per run' },
			{ label: 'Bookmarks', value: 'Favorites sync to production' },
			{ label: 'Analytics', value: 'Trait heatmaps + rarity curves' }
		]
	},
	{
		tag: 'Deployment',
		title: 'Multi-Chain Export + Mint Ops',
		description: 'Publish to Solana, Ethereum, Base, and Abstract with zero manual schema mapping, bundled metadata validation, and CDN-backed delivery.',
		longDescription: 'Deployment tooling orchestrates everything from metadata validation to distribution logistics. Launch across multiple chains without duplicating configuration work.',
		subFeatures: [
			{
				title: 'Schema Mapping Automation',
				details: 'Translate metadata to Metaplex, ERC-721/1155, or custom schemas automatically, with validation reports for each chain.'
			},
			{
				title: 'Mint Operations',
				details: 'Batch export ZIPs, queue on-chain mints, and send webhook notifications when each tranche clears.'
			},
			{
				title: 'Distribution & Storage',
				details: 'Push assets to redundant storage with CDN acceleration, IPFS pinning, and download manifests.'
			}
		],
		icon: <PublicIcon sx={{ fontSize: 44, color: '#3498db' }} />,
		proofPoints: [
			{ label: 'Standards', value: 'Metaplex, ERC-721/1155, custom' },
			{ label: 'Throughput', value: '10K NFTs in under 11 minutes server-side' },
			{ label: 'Storage', value: 'Global CDN with redundancy' }
		]
	}
];

export const faqs = [
	{
		question: 'How does pricing work?',
		answer: `GraveForge is free to use for building, previewing, and testing your NFT collections. You only pay when you export watermark-free assets. The base price is $${USD_PRICE_PER_TOKEN.toFixed(2)} per NFT. Server-side generation (for large collections) costs 1.33x the base price. Pay with SOL, ETH, DEADS token (10% discount), or credit card.`
	},
	{
		question: 'What file formats are supported?',
		answer: 'GraveForge supports PNG (recommended for transparency), JPG, JPEG, and WebP formats. For best results, use PNG files with transparent backgrounds. Images should be at least 2000x2000px for high-quality output.'
	},
	{
		question: 'Can I collaborate with my team?',
		answer: 'Yes! Invite team members by wallet address and assign roles (Admin, Editor, Viewer, or Custom). Permissions include editing layers/images, generating previews, managing team, and exporting collections. Real-time sync keeps everyone updated.'
	},
	{
		question: 'What blockchains are supported?',
		answer: 'GraveForge supports Solana (Metaplex standard), Ethereum (ERC-721/1155), Base, and Abstract. Metadata is automatically formatted for your target blockchain with built-in validation.'
	},
	{
		question: 'How do decision trees work?',
		answer: 'Decision trees (paths) let you create multiple distinct variants within one project. Each path defines which layers are active and which images are available. Paths can have different weights (probabilities) and conditional triggers based on selected traits.'
	},
	{
		question: 'What is the difference between client-side and server-side generation?',
		answer: 'Client-side runs in your browser - free, private, good for <1000 NFTs. Server-side runs on our infrastructure - faster, handles 50K+ collections, includes watermarking, costs 1.33x base price. Large collections (10K+) are background jobs you can close your browser during.'
	},
	{
		question: 'Can I export my project configuration?',
		answer: 'Yes! Export creates an encrypted .bfp file containing all project data, images, and configuration. Import recreates the project exactly. Use this for backups, transferring between accounts, or sharing templates with your team.'
	},
	{
		question: 'Do you offer volume discounts?',
		answer: 'Yes! Volume discounts: 10K+ NFTs = 10% off, 25K+ = 15% off, 50K+ = 20% off. Additional 10% discount when paying with DEADS token (stackable with volume discounts). Discounts apply automatically at checkout.'
	}
];

export const documentationSections = [
	{
		id: 'getting-started',
		title: 'Getting Started',
		icon: <CloudUploadIcon />,
		color: '#ef4444',
		topics: [
			{ q: 'How do I create my first project?', a: 'Connect your wallet, click "New Project" from the dashboard, enter your project details (name, description, blockchain), and click Create. You\'ll be taken to the project builder where you can start adding layers and uploading images.' },
			{ q: 'What file formats are supported?', a: 'GraveForge supports PNG (recommended for transparency), JPG, and WebP formats. For best results, use PNG files with transparent backgrounds. Images should be at least 2000x2000px for high-quality output.' },
			{ q: 'How do I upload images?', a: 'Navigate to Layer Manager, create a new layer, then drag-and-drop images or click to browse. You can upload multiple images at once. Each image is automatically processed and thumbnails are generated.' }
		]
	},
	{
		id: 'layer-management',
		title: 'Layer Management',
		icon: <LayersIcon />,
		color: '#14F195',
		topics: [
			{ q: 'What are layers and how do they work?', a: 'Layers are organizational containers for your NFT traits. Each layer represents a trait category (like Background, Body, Eyes). During generation, one image from each active layer is randomly selected and composited to create the final NFT.' },
			{ q: 'How do I reorder layers?', a: 'Use drag-and-drop to reorder layers. The order determines how images composite - layers at the bottom render first, layers at the top render last. This is crucial for proper visual stacking.' },
			{ q: 'Can I make a layer optional?', a: 'Yes! When configuring rarity, you can add a "None" option with a specific probability. This makes the layer appear only in a percentage of generated NFTs, creating rarity through absence.' }
		]
	},
	{
		id: 'rarity-configuration',
		title: 'Rarity Configuration',
		icon: <SettingsIcon />,
		color: '#646cff',
		topics: [
			{ q: 'How does percentage-based rarity work?', a: 'Assign each image a weight (0-100%). The system normalizes these weights so they sum to 100%, then uses them as selection probabilities. An image with 50% weight is twice as likely to be selected as one with 25%.' },
			{ q: 'What is tier-based rarity?', a: 'Tier mode uses predefined categories: Common (60%), Uncommon (25%), Rare (10%), Epic (4%), Legendary (1%). Assign each image to a tier, and the system automatically calculates probabilities.' },
			{ q: 'How can I preview rarity distribution?', a: 'The Rarity Analytics panel shows expected trait distribution for any collection size. It displays percentage breakdowns, rarity scores, and highlights potential issues like impossible combinations.' }
		]
	},
	{
		id: 'decision-trees-paths',
		title: 'Decision Trees & Paths',
		icon: <TreeIcon />,
		color: '#ff6b35',
		topics: [
			{ q: 'What are generation paths?', a: 'Paths are preset configurations that define which layers are active and which images are available. They enable creating multiple distinct variants within one project (e.g., "Warriors," "Mages," "Rogues").' },
			{ q: 'How do I create a path?', a: 'In Path Manager, click "New Path", give it a name and weight (probability), then configure which layers are included and which images are available. You can also set path-specific rules.' },
			{ q: 'What are conditional paths?', a: 'Conditional paths evaluate criteria during generation and switch paths based on selected traits. For example, selecting "Dragon" could automatically switch to a dragon-specific path with appropriate accessories.' }
		]
	},
	{
		id: 'rules-engine',
		title: 'Rules Engine',
		icon: <SecurityIcon />,
		color: '#9b59b6',
		topics: [
			{ q: 'What types of rules can I create?', a: 'Four types: Inclusion (if A then B must appear), Exclusion (A and B cannot appear together), Requirement (A requires at least one from set B), and Probability Modification (if A then increase/decrease chance of B).' },
			{ q: 'How do I create a rule?', a: 'Navigate to Rules Engine, click "New Rule", select rule type, choose trigger image(s), select target image(s), and set conditions. The visual builder shows connections between images.' },
			{ q: 'What are rule chains?', a: 'Rule chains allow rules to trigger other rules in sequence. Example: "Cyberpunk Background" → increases tech accessory probability → triggers neon colors → modifies clothing style.' }
		]
	},
	{
		id: 'preview-testing',
		title: 'Preview & Testing',
		icon: <VisibilityIcon />,
		color: '#3498db',
		topics: [
			{ q: 'How do I generate previews?', a: 'Click "Generate Preview", specify how many samples (1-100), and click Generate. Previews use the same logic as final generation, showing exactly how your collection will look.' },
			{ q: 'Can I save specific previews?', a: 'Yes! Click the bookmark icon on any preview to save it as a reference. Saved previews can be viewed later, shared with team members, or used as examples.' },
			{ q: 'What is A/B testing?', a: 'A/B testing lets you generate previews with different configuration variants side-by-side. Compare different rarity schemes, rule sets, or path configurations before committing.' }
		]
	},
	{
		id: 'generation-export',
		title: 'Generation & Export',
		icon: <DownloadIcon />,
		color: '#e74c3c',
		topics: [
			{ q: 'Client-side vs Server-side generation?', a: 'Client-side runs in your browser - free, private, good for <1000 NFTs. Server-side runs on our infrastructure - faster, handles 50K+ collections, includes watermarking, costs 1.33x base price.' },
			{ q: 'How long does generation take?', a: 'Client-side: ~100-500 NFTs/minute. Server-side: ~1000-5000 NFTs/minute with parallel processing. Large collections (10K+) are background jobs - you can close your browser and return later.' },
			{ q: 'What metadata formats are supported?', a: 'Solana (Metaplex), Ethereum (ERC-721/1155), and custom schemas. Metadata includes name, description, image URL, attributes array, rarity scores, and optional custom fields.' },
			{ q: 'What is steganography watermarking?', a: 'Available in server-side mode, this embeds invisible authentication data into pixel values. Watermarks survive compression/resizing and cannot be removed by stripping metadata.' }
		]
	},
	{
		id: 'team-collaboration',
		title: 'Team Collaboration',
		icon: <GroupIcon />,
		color: '#f39c12',
		topics: [
			{ q: 'How do I invite team members?', a: 'In Project Settings → Team tab, enter the wallet address of your collaborator, assign a role (Admin, Editor, Viewer, or Custom), and send the invite. They\'ll receive a notification.' },
			{ q: 'What permissions can I grant?', a: 'Permissions include: edit/delete layers, edit/delete images, generate previews, update project info, manage team, and export collections. Custom roles let you create granular permission sets.' },
			{ q: 'Can multiple people edit simultaneously?', a: 'Yes! Real-time synchronization ensures all team members see the latest changes. The system handles conflicts automatically with notifications to both users.' }
		]
	},
	{
		id: 'payment-licensing',
		title: 'Payment & Licensing',
		icon: <PaymentIcon />,
		color: '#1abc9c',
		topics: [
			{ q: 'How does pricing work?', a: `Base price is $${USD_PRICE_PER_TOKEN.toFixed(2)} per NFT. Pay only for what you export - building and previewing is free. Server-side generation costs 1.33x base price. Pay with SOL, ETH, DEADS token (10% discount), or credit card (3% fee).` },
			{ q: 'What payment methods are accepted?', a: 'Cryptocurrency: SOL, ETH, DEADS token. Credit Card: Visa, Mastercard, Amex via Stripe. Crypto payments are processed on-chain with instant confirmation.' },
			{ q: 'Do I get a discount for large collections?', a: 'Yes! Volume discounts: 10K+ NFTs = 10% off, 25K+ = 15% off, 50K+ = 20% off. Additional 10% discount when paying with DEADS token (stackable with volume discounts).' }
		]
	},
	{
		id: 'advanced-features',
		title: 'Advanced Features',
		icon: <CodeIcon />,
		color: '#34495e',
		topics: [
			{ q: 'Can I export/import projects?', a: 'Yes! Export creates an encrypted .bfp file containing all project data, images, and configuration. Import recreates the project exactly. Use this for backups, transferring between accounts, or sharing templates.' },
			{ q: 'Is there an API for automation?', a: 'Yes! The REST API supports project creation, image upload, configuration management, generation triggering, and result retrieval. Use it for CI/CD integration or building custom tools.' },
			{ q: 'How do I handle very large collections (100K+)?', a: 'For massive collections: use server-side generation, enable background processing, configure checkpointing (resume if interrupted), use batch export (multiple ZIP files), and consider our enterprise plan.' }
		]
	}
];

export const pricingHighlights = [
	{
		label: 'Free Tier',
		description: 'Build unlimited projects, configure layers, paths, and rules — completely free. Preview and test your collection before committing.',
		icon: <AutoAwesomeIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
	},
	{
		label: 'Gravemint Integration Required',
		description: 'Export and mint through Gravemint (our launchpad). GraveForge handles generation, Gravemint handles deployment and minting to any supported chain.',
		icon: <MonetizationOnIcon sx={{ fontSize: 32, color: '#dc2626' }} />
	},
	{
		label: 'Server-Side Generation',
		description: 'Massive collections process on GraveForge infrastructure with pixel watermarking, duplicate insurance, and multi-format support.',
		icon: <SpeedIcon sx={{ fontSize: 32, color: '#60a5fa' }} />
	}
];

export const pricingSupport = [
	{
		title: 'Free Tier Includes',
		items: ['Unlimited projects and layers', 'Full path and rules engine', 'Preview generation', 'Project import/export (.bfp)'],
		accent: '#14F195'
	},
	{
		title: 'Gravemint Deployment',
		items: ['Multi-chain minting (Solana, Ethereum, Polygon, Base, etc.)', 'On-chain metadata and IPFS/Arweave uploads', 'Mint pages, allowlists, and revenue management'],
		accent: '#dc2626'
	},
	{
		title: 'DEADS Token Bonus',
		items: ['Pay with DEADS for an extra 10% savings', 'Stackable with volume tiers', 'Automatic recognition at checkout'],
		accent: '#a78bfa'
	}
];

export { USD_PRICE_PER_TOKEN };
